import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GapAnalysis, ImportJob, ImportOverview } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  LoadingPane,
  PageHeader,
  Table,
  formatBytes,
  formatDateTime,
  useToast,
} from '../../components/ui.js';
import './import.css';

const ACCEPT = '.txt,.md,.csv,.docx,.xlsx,.pdf';

function matchesSummary(job: ImportJob): string {
  if (job.status === 'failed') return job.error ?? 'Processing failed';
  if (job.matches.length === 0) return 'No corpus matches';
  const counts = { created: 0, updated: 0, skipped: 0 };
  for (const m of job.matches) counts[m.action] += 1;
  return (['created', 'updated', 'skipped'] as const)
    .filter((action) => counts[action] > 0)
    .map((action) => `${counts[action]} ${action}`)
    .join(' · ');
}

const MATCH_ACTION_TONE = { created: 'success', updated: 'info', skipped: 'neutral' } as const;
const GAP_STATUS_TONE = { missing: 'danger', draft: 'warning', approved: 'success' } as const;

export function ImportPage() {
  const { me } = useAuth();
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<ReadonlySet<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Mirrors the worker's RBAC: admins/contributors can upload, resync and
  // delete; reviewers get read-only access to the overview and gap analysis.
  const canWrite =
    org.role === 'admin' || org.role === 'contributor' || (me?.user.isSystemAdmin ?? false);
  const canView = canWrite || org.role === 'reviewer';
  // Organisation AI switch: the document uploader is AI extraction, hidden
  // when off; gap analysis, resync and the processed-documents list remain.
  const aiEnabled = org.aiEnabled !== false;

  const overview = useQuery({
    queryKey: ['import-overview', org.orgId],
    queryFn: () => apiFetch<ImportOverview>(`/orgs/${org.orgId}/import`),
    enabled: canView,
  });
  const gap = useQuery({
    queryKey: ['import-gap-analysis', org.orgId],
    queryFn: () => apiFetch<GapAnalysis>(`/orgs/${org.orgId}/import/gap-analysis`),
    enabled: canView,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['import-overview', org.orgId] });
    void queryClient.invalidateQueries({ queryKey: ['import-gap-analysis', org.orgId] });
    void queryClient.invalidateQueries({ queryKey: ['corpus', org.orgId] });
  };
  const onError = (err: unknown) => {
    if (err instanceof ApiRequestError && err.code === 'not_configured') {
      toast(
        'AI extraction is not configured on this environment — an OPENAI_API_KEY is required before documents can be imported.',
        'error',
      );
      return;
    }
    toast(err instanceof ApiRequestError ? err.message : 'Import action failed', 'error');
  };

  const upload = useMutation({
    mutationFn: (toUpload: File[]) => {
      const formData = new FormData();
      for (const file of toUpload) formData.append('file', file);
      return apiFetch<{ jobs: ImportJob[] }>(`/orgs/${org.orgId}/import/documents`, {
        method: 'POST',
        formData,
      });
    },
    onSuccess: ({ jobs }) => {
      const matches = jobs.reduce((sum, job) => sum + job.matches.length, 0);
      toast(
        `Processed ${jobs.length} document${jobs.length === 1 ? '' : 's'} — ${matches} corpus match${matches === 1 ? '' : 'es'}.`,
        'success',
      );
      setFiles([]);
      invalidate();
    },
    onError,
  });

  const deleteJobs = useMutation({
    mutationFn: (jobIds: string[]) =>
      apiFetch<{ deleted: number }>(`/orgs/${org.orgId}/import/jobs/delete`, {
        method: 'POST',
        body: { jobIds },
      }),
    onSuccess: ({ deleted }) => {
      toast(`${deleted} document${deleted === 1 ? '' : 's'} deleted.`, 'success');
      setSelectedJobIds(new Set());
      setConfirmDelete(false);
      invalidate();
    },
    onError: (err: unknown) => {
      setConfirmDelete(false);
      onError(err);
    },
  });

  const resync = useMutation({
    mutationFn: () =>
      apiFetch<{ assessmentsScanned: number; responsesPrefilled: number }>(
        `/orgs/${org.orgId}/import/resync`,
        { method: 'POST' },
      ),
    onSuccess: (result) => {
      toast(
        `${result.responsesPrefilled} response${result.responsesPrefilled === 1 ? '' : 's'} pre-filled across ${result.assessmentsScanned} assessment${result.assessmentsScanned === 1 ? '' : 's'}.`,
        'success',
      );
      invalidate();
    },
    onError,
  });

  if (!canView) {
    return (
      <EmptyState
        title="Not available"
        message="Import & migrate is available to organisation admins, contributors and reviewers."
      />
    );
  }
  if (overview.isError) {
    return (
      <div>
        <PageHeader title="Import & migrate" />
        <EmptyState
          title="Could not load the import overview"
          message="Something went wrong loading imported documents — please try again shortly."
          action={<Button onClick={() => void overview.refetch()}>Retry</Button>}
        />
      </div>
    );
  }
  if (overview.isLoading) return <LoadingPane label="Loading import overview" />;

  const addFiles = (incoming: FileList | null) => {
    // The upload mutation captures the queue at click time, so the queue must
    // not change while a batch is in flight.
    if (upload.isPending) return;
    if (!incoming || incoming.length === 0) return;
    setFiles((current) => {
      const merged = [...current];
      for (const file of Array.from(incoming)) {
        if (!merged.some((f) => f.name === file.name && f.size === file.size)) merged.push(file);
      }
      return merged;
    });
  };

  const jobs = overview.data?.jobs ?? [];
  const newAssessments = overview.data?.newAssessmentsSinceImport ?? [];

  const allJobsSelected = jobs.length > 0 && jobs.every((job) => selectedJobIds.has(job.id));
  const toggleJobSelected = (id: string) =>
    setSelectedJobIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div>
      <PageHeader
        title="Import & migrate"
        subtitle="Bring existing documents in and build your shared corpus automatically"
      />

      {canWrite && !aiEnabled && (
        <Card title="Import documents">
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>AI import is turned off in Organisation settings</p>
        </Card>
      )}

      {canWrite && aiEnabled && (
      <Card title="Import documents">
        <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
          Upload your existing policies, procedures and registers — AI extracts the GRC-relevant
          content and files it into your shared corpus.
        </p>
        <div
          className={`import-dropzone ${dragOver ? 'drag-over' : ''} ${upload.isPending ? 'disabled' : ''}`}
          aria-disabled={upload.isPending}
          onDragOver={(e) => {
            e.preventDefault();
            if (!upload.isPending) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
        >
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPT}
            className="sr-only"
            aria-label="Choose documents to import"
            disabled={upload.isPending}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <p className="import-dropzone-hint">
            Drag and drop files here, or{' '}
            <Button
              variant="secondary"
              size="sm"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              Choose files
            </Button>
          </p>
          <p className="import-dropzone-formats">Text, Markdown, CSV, Word, Excel or PDF documents</p>
        </div>
        {files.length > 0 && (
          <ul className="import-file-list">
            {files.map((file) => (
              <li key={`${file.name}-${file.size}`}>
                <span className="import-file-name">{file.name}</span>
                <span className="import-file-size">{formatBytes(file.size)}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${file.name}`}
                  disabled={upload.isPending}
                  onClick={() => setFiles((current) => current.filter((f) => f !== file))}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
          <Button
            loading={upload.isPending}
            disabled={files.length === 0}
            onClick={() => upload.mutate(files)}
          >
            Upload {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'}` : ''}
          </Button>
          {upload.isPending && (
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
              Uploading and extracting content — this can take a minute for larger documents.
            </span>
          )}
        </div>

        <div className="import-connectors">
          {(overview.data?.connectors ?? []).map((connector) => (
            <div key={connector.key} className="import-connector">
              <Button variant="secondary" disabled>
                Connect {connector.label}
              </Button>
              <p className="import-connector-reason">{connector.reason}</p>
            </div>
          ))}
        </div>
      </Card>
      )}

      {canWrite && newAssessments.length > 0 && (
        <Card className="import-resync" title="New frameworks since your last import">
          <p style={{ marginTop: 0 }}>
            {newAssessments.length} framework{newAssessments.length === 1 ? '' : 's'} started since
            your last import. Resync to pre-fill their empty responses from your imported corpus.
          </p>
          <ul className="import-resync-list">
            {newAssessments.map((a) => (
              <li key={a.assessmentId}>
                <strong>{a.name}</strong> — {a.standardName} {a.versionLabel} · started{' '}
                {formatDateTime(a.createdAt)}
              </li>
            ))}
          </ul>
          <Button loading={resync.isPending} onClick={() => resync.mutate()}>
            Resync now
          </Button>
        </Card>
      )}

      <Card
        title="Processed documents"
        actions={
          canWrite && jobs.length > 0 ? (
            <Button
              variant="danger"
              size="sm"
              disabled={selectedJobIds.size === 0}
              onClick={() => setConfirmDelete(true)}
            >
              Delete selected{selectedJobIds.size > 0 ? ` (${selectedJobIds.size})` : ''}
            </Button>
          ) : undefined
        }
      >
        {jobs.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>
            {canWrite && aiEnabled
              ? 'No documents imported yet — upload your first batch above.'
              : 'No documents have been imported yet.'}
          </p>
        ) : (
          <Table
            head={
              <>
                {canWrite && (
                  <th className="import-select-col">
                    <input
                      type="checkbox"
                      aria-label="Select all documents"
                      checked={allJobsSelected}
                      onChange={() =>
                        setSelectedJobIds(
                          allJobsSelected ? new Set() : new Set(jobs.map((j) => j.id)),
                        )
                      }
                    />
                  </th>
                )}
                <th>File</th>
                <th>When</th>
                <th>Status</th>
                <th>Matches</th>
                <th>
                  <span className="sr-only">Details</span>
                </th>
              </>
            }
          >
            {jobs.map((job) => {
              const expanded = expandedJobId === job.id;
              const toggle = () => setExpandedJobId(expanded ? null : job.id);
              return [
                <tr key={job.id} className="import-job-row" onClick={toggle}>
                  {canWrite && (
                    <td className="import-select-col" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${job.fileName}`}
                        checked={selectedJobIds.has(job.id)}
                        onChange={() => toggleJobSelected(job.id)}
                      />
                    </td>
                  )}
                  <td>
                    {job.fileName}
                    <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                      {formatBytes(job.sizeBytes)}
                      {job.createdByName ? ` · ${job.createdByName}` : ''}
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(job.createdAt)}</td>
                  <td>
                    <Badge tone={job.status === 'succeeded' ? 'success' : 'danger'}>
                      {job.status}
                    </Badge>
                  </td>
                  <td>{matchesSummary(job)}</td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-expanded={expanded}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle();
                      }}
                    >
                      {expanded ? 'Hide' : 'Details'}
                    </Button>
                  </td>
                </tr>,
                expanded && (
                  <tr key={`${job.id}-details`} className="import-job-details">
                    <td colSpan={canWrite ? 6 : 5}>
                      {job.matches.length === 0 ? (
                        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                          {job.status === 'failed'
                            ? (job.error ?? 'Processing failed.')
                            : 'Nothing in this document matched a corpus item.'}
                        </p>
                      ) : (
                        <ul className="import-match-list">
                          {job.matches.map((match, i) => (
                            <li key={`${match.corpusKey}-${i}`}>
                              <code className="import-code-chip">{match.corpusKey}</code>
                              <Badge tone={MATCH_ACTION_TONE[match.action]}>{match.action}</Badge>
                              <span>
                                <strong>{match.title}</strong> — {match.summary}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ),
              ];
            })}
          </Table>
        )}
        {canWrite && (
          <Dialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            title="Delete selected documents"
          >
            <p>
              Delete {selectedJobIds.size} processed document
              {selectedJobIds.size === 1 ? '' : 's'}? The uploaded files are removed permanently —
              corpus content created from them is kept.
            </p>
            <div className="dialog-actions">
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={deleteJobs.isPending}
                onClick={() => deleteJobs.mutate([...selectedJobIds])}
              >
                Delete
              </Button>
            </div>
          </Dialog>
        )}
      </Card>

      <Card
        title="Gap analysis"
        actions={
          <Button
            variant="secondary"
            size="sm"
            loading={gap.isFetching}
            onClick={() => void gap.refetch()}
          >
            Refresh
          </Button>
        }
      >
        {gap.isLoading ? (
          <LoadingPane label="Loading gap analysis" />
        ) : gap.isError || !gap.data ? (
          <p style={{ color: 'var(--color-text-muted)' }}>
            Could not load the gap analysis — please try again shortly.
          </p>
        ) : (
          <>
            <h4 className="import-gap-heading">Corpus coverage</h4>
            {gap.data.corpusGaps.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>
                No corpus items are referenced by your active assessments yet.
              </p>
            ) : (
              <Table
                head={
                  <>
                    <th>Item</th>
                    <th>Status</th>
                    <th>Referenced by</th>
                  </>
                }
              >
                {gap.data.corpusGaps.map((item) => (
                  <tr key={item.key}>
                    <td>
                      {item.title}
                      <div>
                        <code className="import-code-chip">{item.key}</code>
                      </div>
                    </td>
                    <td>
                      <Badge tone={GAP_STATUS_TONE[item.status]}>{item.status}</Badge>
                    </td>
                    <td>{item.referencedBy.length > 0 ? item.referencedBy.join(', ') : '—'}</td>
                  </tr>
                ))}
              </Table>
            )}

            <h4 className="import-gap-heading">Assessments</h4>
            {gap.data.assessments.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>No active assessments to analyse.</p>
            ) : (
              <ul className="import-gap-assessments">
                {gap.data.assessments.map((a) => (
                  <li key={a.assessmentId}>
                    <div>
                      <strong>{a.name}</strong>{' '}
                      <span style={{ color: 'var(--color-text-muted)' }}>
                        {a.standardName} {a.versionLabel}
                      </span>
                    </div>
                    <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                      {a.emptyResponses} of {a.totalResponses} response
                      {a.totalResponses === 1 ? '' : 's'} empty
                    </div>
                    {a.missingCorpusKeys.length > 0 && (
                      <div className="import-gap-keys">
                        {a.missingCorpusKeys.map((key) => (
                          <code key={key} className="import-code-chip">
                            {key}
                          </code>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
              Generated {formatDateTime(gap.data.generatedAt)}
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
